/**
 * Work Order Status List API Contract
 *
 * ts-rest contract definition for the job work order status list endpoint.
 *
 * Endpoint  : GET /jobs/work-order-status-list
 * Method    : GET
 * Auth      : Required (standard bearer token via middleware)
 * Response  : Array of active job work order statuses that are in use by at
 *             least one job, ordered by sequence
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for a single work order status record
 */
export const workOrderStatusItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  sequence: z.number().nullable(),
  is_active: z.boolean().nullable(),
});

/**
 * Zod schema for the work order status list response
 */
export const workOrderStatusListResponseSchema = z.array(workOrderStatusItemSchema);

/**
 * TypeScript types inferred from Zod schemas
 */
export type WorkOrderStatusItem = z.infer<typeof workOrderStatusItemSchema>;
export type WorkOrderStatusListResponse = z.infer<typeof workOrderStatusListResponseSchema>;

/**
 * Work Order Status List API Contract
 *
 * Base path: /jobs/work-order-status-list  (mounted in apps/backend/src/routes/index.ts)
 */
export const workOrderStatusListContract = c.router({
  /**
   * Get active, in-use job work order statuses
   *
   * GET /jobs/work-order-status-list
   *
   * Returns job_work_order_status rows where is_active = true AND the status
   * is assigned to at least one non-deleted job, sorted by sequence
   * ascending (SC-6721).
   *
   * Used by: works-lite work order status filter dropdown.
   */
  getWorkOrderStatusList: {
    method: 'GET',
    path: '/',
    responses: {
      200: workOrderStatusListResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get active, in-use job work order statuses ordered by sequence',
    description:
      'Returns job_work_order_status rows where is_active = true and the status is assigned to at least one non-deleted job, ordered by sequence ascending.',
  },
});
